/**
 * Story Management — مدیریت و مدراسیون استوری‌های فروشگاه‌ها
 *
 * دو تب: «استوری‌ها» (فعال / گزارش‌شده / مخفی‌شده + جستجوی فروشگاه) و
 * «گزارش‌ها» (لیست همهٔ گزارش‌های کاربران با snapshot — حتی بعد از انقضای
 * ۲۴ ساعتهٔ استوری).
 *
 * اکشن‌ها: مشاهده تصویر (لایت‌باکس)، خروج از انتشار / بازگرداندن به انتشار،
 * حذف کامل، و بررسی/رد گزارش‌ها.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Dialog, DialogContent, CircularProgress, Tooltip } from '@mui/material';
import AutoStoriesIcon from '@mui/icons-material/AutoStories';
import SearchIcon from '@mui/icons-material/Search';
import RefreshIcon from '@mui/icons-material/Refresh';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import CloseIcon from '@mui/icons-material/Close';
import InboxIcon from '@mui/icons-material/Inbox';
import DeleteIcon from '@mui/icons-material/Delete';
import FlagIcon from '@mui/icons-material/Flag';
import ImageIcon from '@mui/icons-material/Image';
import StorefrontIcon from '@mui/icons-material/Storefront';
import PersonIcon from '@mui/icons-material/Person';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import RemoveRedEyeIcon from '@mui/icons-material/RemoveRedEye';
import PublishIcon from '@mui/icons-material/Publish';
import moment from 'moment-jalaali';

import storyAdminService, {
  AdminStory,
  AdminStoryReport,
  StoryStatusFilter,
  StoryReportStatus,
  REPORT_REASON_LABELS,
  REPORT_STATUS_LABELS
} from '../services/storyAdmin.service';
import { toPersianDigits } from '../components/common/JalaliDatePicker';

import './EntityManagement.css';

type MainTab = 'stories' | 'reports';

const storyStatusOptions: { value: StoryStatusFilter; label: string }[] = [
  { value: '', label: 'همه' },
  { value: 'active', label: 'فعال' },
  { value: 'reported', label: 'گزارش‌شده' },
  { value: 'hidden', label: 'مخفی‌شده' }
];

const reportStatusOptions: { value: StoryReportStatus | ''; label: string }[] = [
  { value: '', label: 'همه وضعیت‌ها' },
  { value: 'pending', label: 'در انتظار بررسی' },
  { value: 'reviewed', label: 'بررسی شد' },
  { value: 'dismissed', label: 'رد شد' },
  { value: 'actioned', label: 'اقدام شد' }
];

const formatJalaliDate = (date?: string): string => {
  if (!date) return '-';
  try {
    return toPersianDigits(moment(date).format('jYYYY/jMM/jDD'));
  } catch {
    return '-';
  }
};

const formatTime = (date?: string): string => {
  if (!date) return '';
  try {
    return toPersianDigits(moment(date).format('HH:mm'));
  } catch {
    return '';
  }
};

const formatJalaliDateTime = (date?: string): string => {
  if (!date) return '-';
  try {
    return toPersianDigits(moment(date).format('jYYYY/jMM/jDD — HH:mm'));
  } catch {
    return '-';
  }
};

const reasonTagClass = (reason: string): string => {
  switch (reason) {
    case 'fraud':
    case 'violence':
      return 'tag tag-sm tag-danger';
    case 'inappropriate':
    case 'copyright':
      return 'tag tag-sm tag-warning';
    default:
      return 'tag tag-sm tag-info';
  }
};

const reportStatusTagClass = (status: StoryReportStatus): string => {
  switch (status) {
    case 'pending':
      return 'tag tag-sm tag-warning';
    case 'reviewed':
      return 'tag tag-sm tag-info';
    case 'dismissed':
      return 'tag tag-sm tag-neutral';
    case 'actioned':
      return 'tag tag-sm tag-success';
    default:
      return 'tag tag-sm tag-neutral';
  }
};

/** تصویر بندانگشتی استوری (تصویر یا ویدیو) */
const StoryThumb: React.FC<{ url?: string; type?: string; size?: number; onClick?: () => void }> = ({
  url,
  type,
  size = 44,
  onClick
}) => {
  const style: React.CSSProperties = {
    width: size,
    height: size,
    borderRadius: 8,
    objectFit: 'cover',
    background: 'var(--color-bg-default)',
    border: '1px solid var(--color-border-light)',
    cursor: onClick ? 'pointer' : 'default',
    display: 'block'
  };
  if (!url) {
    return (
      <span
        style={{
          ...style,
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'var(--color-text-secondary)'
        }}
      >
        <ImageIcon fontSize="small" />
      </span>
    );
  }
  if (type === 'video') {
    return <video src={url} style={style} onClick={onClick} muted />;
  }
  return <img src={url} alt="" style={style} onClick={onClick} />;
};

const StoryManagement: React.FC = () => {
  const [mainTab, setMainTab] = useState<MainTab>('stories');
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // ---------- Stories tab state ----------
  const [stories, setStories] = useState<AdminStory[]>([]);
  const [storiesLoading, setStoriesLoading] = useState(true);
  const [storiesTotal, setStoriesTotal] = useState(0);
  const [storiesPage, setStoriesPage] = useState(0);
  const [storiesPerPage, setStoriesPerPage] = useState(20);
  const [storyStatus, setStoryStatus] = useState<StoryStatusFilter>('');
  const [searchInput, setSearchInput] = useState('');
  const [searchTerm, setSearchTerm] = useState('');

  // ---------- Reports tab state ----------
  const [reports, setReports] = useState<AdminStoryReport[]>([]);
  const [reportsLoading, setReportsLoading] = useState(false);
  const [reportsTotal, setReportsTotal] = useState(0);
  const [reportsPage, setReportsPage] = useState(0);
  const [reportsPerPage, setReportsPerPage] = useState(20);
  const [reportStatusFilter, setReportStatusFilter] = useState<StoryReportStatus | ''>('');

  // ---------- Dialogs ----------
  const [lightboxStory, setLightboxStory] = useState<AdminStory | null>(null);
  const [lightboxUrl, setLightboxUrl] = useState<{ url: string; type?: string } | null>(null);

  const [moderateStory, setModerateStory] = useState<AdminStory | null>(null);
  const [moderateHide, setModerateHide] = useState(true);
  const [moderateReason, setModerateReason] = useState('');
  const [moderating, setModerating] = useState(false);

  const [deleteStory, setDeleteStory] = useState<AdminStory | null>(null);
  const [deleting, setDeleting] = useState(false);

  const [reportsDrawerStory, setReportsDrawerStory] = useState<AdminStory | null>(null);
  const [drawerReports, setDrawerReports] = useState<AdminStoryReport[]>([]);
  const [drawerLoading, setDrawerLoading] = useState(false);

  const [reportActionBusy, setReportActionBusy] = useState<string | null>(null);

  const flashSuccess = (msg: string) => {
    setSuccess(msg);
    setTimeout(() => setSuccess(null), 3000);
  };

  // ---------- Loaders ----------
  const loadStories = useCallback(async () => {
    setStoriesLoading(true);
    setError(null);
    try {
      const res = await storyAdminService.getStories(storiesPage + 1, storiesPerPage, {
        status: storyStatus,
        search: searchTerm
      });
      setStories(res.stories);
      setStoriesTotal(res.pagination.total);
    } catch (err) {
      setError('خطا در بارگذاری استوری‌ها');
      console.error(err);
    } finally {
      setStoriesLoading(false);
    }
  }, [storiesPage, storiesPerPage, storyStatus, searchTerm]);

  const loadReports = useCallback(async () => {
    setReportsLoading(true);
    setError(null);
    try {
      const res = await storyAdminService.getReports(reportsPage + 1, reportsPerPage, {
        status: reportStatusFilter
      });
      setReports(res.reports);
      setReportsTotal(res.pagination.total);
    } catch (err) {
      setError('خطا در بارگذاری گزارش‌ها');
      console.error(err);
    } finally {
      setReportsLoading(false);
    }
  }, [reportsPage, reportsPerPage, reportStatusFilter]);

  useEffect(() => {
    loadStories();
  }, [loadStories]);

  useEffect(() => {
    if (mainTab === 'reports') loadReports();
  }, [mainTab, loadReports]);

  const loadDrawerReports = useCallback(async (storyId: string) => {
    setDrawerLoading(true);
    try {
      const res = await storyAdminService.getReports(1, 100, { storyId });
      setDrawerReports(res.reports);
    } catch (err) {
      console.error(err);
      setDrawerReports([]);
    } finally {
      setDrawerLoading(false);
    }
  }, []);

  // ---------- Actions ----------
  const handleSearch = () => {
    setSearchTerm(searchInput);
    setStoriesPage(0);
  };

  const openModerate = (story: AdminStory, hide: boolean) => {
    setModerateStory(story);
    setModerateHide(hide);
    setModerateReason('');
  };

  const handleModerate = async () => {
    if (!moderateStory) return;
    setModerating(true);
    setError(null);
    try {
      await storyAdminService.setModeration(
        moderateStory._id,
        moderateHide,
        moderateHide ? moderateReason : undefined
      );
      flashSuccess(moderateHide ? 'استوری از انتشار خارج شد' : 'استوری به انتشار بازگردانده شد');
      setModerateStory(null);
      loadStories();
      if (mainTab === 'reports') loadReports();
    } catch (err: any) {
      setError(err?.message || 'خطا در انجام عملیات');
    } finally {
      setModerating(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteStory) return;
    setDeleting(true);
    setError(null);
    try {
      await storyAdminService.deleteStory(deleteStory._id);
      flashSuccess('استوری حذف شد');
      setDeleteStory(null);
      loadStories();
    } catch (err: any) {
      setError(err?.message || 'خطا در حذف استوری');
    } finally {
      setDeleting(false);
    }
  };

  const handleReportAction = async (
    report: AdminStoryReport,
    status: StoryReportStatus,
    reloadDrawer?: string
  ) => {
    setReportActionBusy(report._id);
    setError(null);
    try {
      await storyAdminService.updateReportStatus(report._id, status);
      flashSuccess(status === 'reviewed' ? 'گزارش بررسی شد' : 'گزارش رد شد');
      if (reloadDrawer) {
        loadDrawerReports(reloadDrawer);
      }
      loadReports();
      loadStories();
    } catch (err: any) {
      setError(err?.message || 'خطا در به‌روزرسانی گزارش');
    } finally {
      setReportActionBusy(null);
    }
  };

  // ---------- Derived ----------
  const storiesPages = Math.ceil(storiesTotal / storiesPerPage);
  const reportsPages = Math.ceil(reportsTotal / reportsPerPage);

  // ---------- Renderers ----------
  const renderReportRow = (report: AdminStoryReport, inDrawer: boolean) => {
    const mediaUrl = report.storyId?.media?.url || report.snapshot?.mediaUrl;
    const storeName = report.storeId?.name || report.snapshot?.storeName || 'نامشخص';
    const storyGone = !report.storyId;
    return (
      <div
        key={report._id}
        style={{
          display: 'flex',
          gap: 12,
          padding: '12px 14px',
          border: '1px solid var(--color-border-light)',
          borderRadius: 10,
          marginBottom: 10,
          background: 'var(--color-bg-paper)',
          alignItems: 'flex-start',
          flexWrap: 'wrap'
        }}
      >
        <StoryThumb
          url={mediaUrl}
          type={report.storyId?.media?.type}
          size={56}
          onClick={mediaUrl ? () => setLightboxUrl({ url: mediaUrl, type: report.storyId?.media?.type }) : undefined}
        />
        <div style={{ flex: 1, minWidth: 220 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 6 }}>
            <span className={reasonTagClass(report.reason)}>
              {REPORT_REASON_LABELS[report.reason] || report.reason}
            </span>
            <span className={reportStatusTagClass(report.status)}>
              {REPORT_STATUS_LABELS[report.status] || report.status}
            </span>
            {storyGone && (
              <span className="tag tag-sm tag-neutral">استوری منقضی/حذف شده</span>
            )}
          </div>
          {!inDrawer && (
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 12, color: 'var(--color-text-secondary)', marginBottom: 4 }}>
              <StorefrontIcon style={{ fontSize: 14 }} />
              {storeName}
            </div>
          )}
          {report.description && (
            <div style={{ fontSize: 13, color: 'var(--color-text-primary)', lineHeight: 1.7, marginBottom: 6 }}>
              {report.description}
            </div>
          )}
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, fontSize: 12, color: 'var(--color-text-secondary)', flexWrap: 'wrap' }}>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
              <PersonIcon style={{ fontSize: 14 }} />
              {report.reporterId?.name || 'کاربر نامشخص'}
              {report.reporterId?.phone && (
                <span style={{ direction: 'ltr', unicodeBidi: 'embed' }}>
                  ({toPersianDigits(report.reporterId.phone)})
                </span>
              )}
            </span>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
              <AccessTimeIcon style={{ fontSize: 14 }} />
              {formatJalaliDateTime(report.createdAt)}
            </span>
            {report.reviewedBy?.name && (
              <span>
                بررسی توسط: {report.reviewedBy.name}
                {report.reviewedAt ? ` — ${formatJalaliDateTime(report.reviewedAt)}` : ''}
              </span>
            )}
          </div>
        </div>
        {report.status === 'pending' && (
          <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
            <Tooltip title="بررسی شد" arrow>
              <button
                className="action-btn toggle active"
                disabled={reportActionBusy === report._id}
                onClick={() => handleReportAction(report, 'reviewed', inDrawer ? reportsDrawerStory?._id : undefined)}
              >
                {reportActionBusy === report._id ? (
                  <CircularProgress size={14} />
                ) : (
                  <CheckCircleIcon />
                )}
              </button>
            </Tooltip>
            <Tooltip title="رد گزارش" arrow>
              <button
                className="action-btn delete"
                disabled={reportActionBusy === report._id}
                onClick={() => handleReportAction(report, 'dismissed', inDrawer ? reportsDrawerStory?._id : undefined)}
              >
                <CancelIcon />
              </button>
            </Tooltip>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="entity-page">
      {/* Header */}
      <div className="entity-header">
        <div>
          <h1 className="entity-title">مدیریت استوری‌ها</h1>
          <p className="entity-subtitle">نظارت بر استوری‌های فروشگاه‌ها و رسیدگی به گزارش‌های کاربران</p>
        </div>
        <div className="header-actions">
          <button
            className="header-btn refresh"
            onClick={() => {
              loadStories();
              if (mainTab === 'reports') loadReports();
            }}
            title="بروزرسانی"
          >
            <RefreshIcon fontSize="small" />
          </button>
        </div>
      </div>

      {error && (
        <div className="tag tag-sm tag-danger" style={{ display: 'block', padding: '10px 14px', marginBottom: 12, fontSize: 13 }}>
          {error}
          <button
            onClick={() => setError(null)}
            style={{ float: 'left', background: 'none', border: 'none', color: 'inherit', cursor: 'pointer', fontSize: 16 }}
          >
            ×
          </button>
        </div>
      )}
      {success && (
        <div className="tag tag-sm tag-success" style={{ display: 'block', padding: '10px 14px', marginBottom: 12, fontSize: 13 }}>
          {success}
        </div>
      )}

      {/* Main tabs */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 14 }}>
        <button
          className={mainTab === 'stories' ? 'filter-btn' : 'filter-btn outline'}
          onClick={() => setMainTab('stories')}
        >
          <AutoStoriesIcon fontSize="small" />
          استوری‌ها
        </button>
        <button
          className={mainTab === 'reports' ? 'filter-btn' : 'filter-btn outline'}
          onClick={() => setMainTab('reports')}
        >
          <FlagIcon fontSize="small" />
          گزارش‌ها
        </button>
      </div>

      {/* ================= STORIES TAB ================= */}
      {mainTab === 'stories' && (
        <>
          {/* Filters */}
          <div className="entity-filters">
            <div className="filters-grid">
              <div className="filter-group">
                <label className="filter-label">
                  <SearchIcon fontSize="small" />
                  جستجوی فروشگاه
                </label>
                <div className="filter-search-wrap">
                  <input
                    className="filter-field"
                    type="text"
                    placeholder="نام فروشگاه..."
                    value={searchInput}
                    onChange={(e) => setSearchInput(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                  />
                  <span className="filter-search-icon">
                    <SearchIcon fontSize="small" />
                  </span>
                </div>
              </div>

              <div className="filter-group">
                <label className="filter-label">وضعیت</label>
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                  {storyStatusOptions.map((opt) => (
                    <button
                      key={opt.value}
                      className={storyStatus === opt.value ? 'filter-btn' : 'filter-btn outline'}
                      style={{ padding: '7px 14px' }}
                      onClick={() => {
                        setStoryStatus(opt.value);
                        setStoriesPage(0);
                      }}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>

              <div className="filter-group filter-group-actions">
                <button className="filter-btn" onClick={handleSearch}>
                  <SearchIcon fontSize="small" />
                  اعمال
                </button>
                {(searchTerm || storyStatus) && (
                  <button
                    className="filter-btn outline"
                    onClick={() => {
                      setSearchInput('');
                      setSearchTerm('');
                      setStoryStatus('');
                      setStoriesPage(0);
                    }}
                  >
                    پاک
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* Table */}
          <div className="entity-table-wrapper">
            {storiesLoading && !stories.length ? (
              <div className="entity-loading">
                <CircularProgress size={32} />
              </div>
            ) : stories.length === 0 ? (
              <div className="entity-empty">
                <div className="entity-empty-icon">
                  <InboxIcon style={{ fontSize: 48 }} />
                </div>
                <p className="entity-empty-text">استوری‌ای یافت نشد</p>
              </div>
            ) : (
              <>
                <table className="entity-table">
                  <thead>
                    <tr>
                      <th>استوری</th>
                      <th>فروشگاه</th>
                      <th>تاریخ انتشار</th>
                      <th>بازدید</th>
                      <th>گزارش‌ها</th>
                      <th>وضعیت</th>
                      <th style={{ width: 190 }}>عملیات</th>
                    </tr>
                  </thead>
                  <tbody>
                    {stories.map((story) => (
                      <tr key={story._id}>
                        <td>
                          <StoryThumb
                            url={story.media?.url}
                            type={story.media?.type}
                            onClick={() => setLightboxStory(story)}
                          />
                        </td>
                        <td>
                          <div className="entity-info-cell">
                            <div className="entity-image">
                              {story.store?.logo ? (
                                <img
                                  src={story.store.logo}
                                  alt=""
                                  style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '50%' }}
                                />
                              ) : (
                                <StorefrontIcon />
                              )}
                            </div>
                            <div className="entity-info">
                              <span className="entity-name">{story.store?.name || 'نامشخص'}</span>
                              {story.store?.slug && (
                                <span className="entity-desc" style={{ direction: 'ltr', unicodeBidi: 'embed', fontSize: 11 }}>
                                  {story.store.slug}
                                </span>
                              )}
                            </div>
                          </div>
                        </td>
                        <td>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                            <span className="entity-desc">{formatJalaliDate(story.createdAt)}</span>
                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3, fontSize: 11, color: 'var(--color-text-secondary)' }}>
                              <AccessTimeIcon style={{ fontSize: 13 }} />
                              {formatTime(story.createdAt)}
                            </span>
                          </div>
                        </td>
                        <td>
                          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 12 }}>
                            <RemoveRedEyeIcon style={{ fontSize: 15, color: 'var(--color-text-secondary)' }} />
                            {toPersianDigits(String(story.viewersCount || 0))}
                          </span>
                        </td>
                        <td>
                          <button
                            onClick={() => {
                              setReportsDrawerStory(story);
                              loadDrawerReports(story._id);
                            }}
                            className={
                              story.pendingReportsCount > 0
                                ? 'tag tag-sm tag-danger'
                                : story.reportsCount > 0
                                  ? 'tag tag-sm tag-warning'
                                  : 'tag tag-sm tag-neutral'
                            }
                            style={{ border: 'none', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 4 }}
                            title="مشاهده گزارش‌های این استوری"
                          >
                            <FlagIcon style={{ fontSize: 13 }} />
                            {toPersianDigits(String(story.reportsCount || 0))}
                            {story.pendingReportsCount > 0 && (
                              <span style={{ fontSize: 10 }}>
                                ({toPersianDigits(String(story.pendingReportsCount))} در انتظار)
                              </span>
                            )}
                          </button>
                        </td>
                        <td>
                          <span className={story.hidden ? 'tag tag-sm tag-danger' : 'tag tag-sm tag-success'}>
                            {story.hidden ? 'مخفی‌شده' : 'منتشر'}
                          </span>
                          {story.hidden && story.hiddenReason && (
                            <Tooltip title={story.hiddenReason} arrow>
                              <span className="entity-desc" style={{ display: 'block', fontSize: 10, maxWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                {story.hiddenReason}
                              </span>
                            </Tooltip>
                          )}
                        </td>
                        <td>
                          <div className="actions-cell">
                            <Tooltip title="مشاهده تصویر" arrow>
                              <button className="action-btn edit" onClick={() => setLightboxStory(story)}>
                                <VisibilityIcon />
                              </button>
                            </Tooltip>
                            {!story.hidden ? (
                              <Tooltip title="خروج از انتشار" arrow>
                                <button className="action-btn delete" onClick={() => openModerate(story, true)}>
                                  <VisibilityOffIcon />
                                </button>
                              </Tooltip>
                            ) : (
                              <Tooltip title="بازگرداندن به انتشار" arrow>
                                <button className="action-btn toggle active" onClick={() => openModerate(story, false)}>
                                  <PublishIcon />
                                </button>
                              </Tooltip>
                            )}
                            <Tooltip title="حذف" arrow>
                              <button className="action-btn delete" onClick={() => setDeleteStory(story)}>
                                <DeleteIcon />
                              </button>
                            </Tooltip>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                {/* Mobile cards */}
                <div className="entity-cards">
                  {stories.map((story) => (
                    <div key={story._id} className="entity-card">
                      <div className="entity-card-header">
                        <StoryThumb url={story.media?.url} type={story.media?.type} size={48} onClick={() => setLightboxStory(story)} />
                        <div className="entity-card-info">
                          <div className="entity-card-name">{story.store?.name || 'نامشخص'}</div>
                          <div className="entity-card-desc">{formatJalaliDateTime(story.createdAt)}</div>
                        </div>
                        <div className="entity-card-status">
                          <span className={story.hidden ? 'tag tag-sm tag-danger' : 'tag tag-sm tag-success'}>
                            {story.hidden ? 'مخفی‌شده' : 'منتشر'}
                          </span>
                        </div>
                      </div>
                      <div style={{ padding: '8px 14px', display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center', fontSize: 12, color: 'var(--color-text-secondary)' }}>
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                          <RemoveRedEyeIcon style={{ fontSize: 14 }} />
                          {toPersianDigits(String(story.viewersCount || 0))} بازدید
                        </span>
                        <span
                          style={{ display: 'inline-flex', alignItems: 'center', gap: 4, color: story.pendingReportsCount > 0 ? '#dc2626' : undefined }}
                        >
                          <FlagIcon style={{ fontSize: 14 }} />
                          {toPersianDigits(String(story.reportsCount || 0))} گزارش
                        </span>
                      </div>
                      <div className="entity-card-actions">
                        <button className="entity-card-btn primary" onClick={() => setLightboxStory(story)}>
                          <VisibilityIcon fontSize="small" /> تصویر
                        </button>
                        <button
                          className="entity-card-btn"
                          onClick={() => {
                            setReportsDrawerStory(story);
                            loadDrawerReports(story._id);
                          }}
                        >
                          <FlagIcon fontSize="small" /> گزارش‌ها
                        </button>
                        {!story.hidden ? (
                          <button className="entity-card-btn danger" onClick={() => openModerate(story, true)}>
                            <VisibilityOffIcon fontSize="small" /> خروج از انتشار
                          </button>
                        ) : (
                          <button className="entity-card-btn" onClick={() => openModerate(story, false)}>
                            <PublishIcon fontSize="small" /> بازگرداندن
                          </button>
                        )}
                        <button className="entity-card-btn danger" onClick={() => setDeleteStory(story)}>
                          <DeleteIcon fontSize="small" /> حذف
                        </button>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Pagination */}
                <div className="entity-pagination">
                  <div className="pagination-info">
                    نمایش {toPersianDigits(String(storiesTotal === 0 ? 0 : storiesPage * storiesPerPage + 1))} تا{' '}
                    {toPersianDigits(String(Math.min((storiesPage + 1) * storiesPerPage, storiesTotal)))} از{' '}
                    {toPersianDigits(String(storiesTotal))}
                  </div>
                  <div className="pagination-controls">
                    <select
                      className="pagination-select"
                      value={storiesPerPage}
                      onChange={(e) => {
                        setStoriesPerPage(Number(e.target.value));
                        setStoriesPage(0);
                      }}
                    >
                      <option value={10}>10</option>
                      <option value={20}>20</option>
                      <option value={50}>50</option>
                    </select>
                    <button
                      className="pagination-btn"
                      onClick={() => setStoriesPage(storiesPage + 1)}
                      disabled={storiesPage >= storiesPages - 1}
                    >
                      <ChevronRightIcon fontSize="small" />
                    </button>
                    <span style={{ fontSize: 12 }}>
                      {toPersianDigits(String(storiesPage + 1))} / {toPersianDigits(String(storiesPages || 1))}
                    </span>
                    <button
                      className="pagination-btn"
                      onClick={() => setStoriesPage(storiesPage - 1)}
                      disabled={storiesPage <= 0}
                    >
                      <ChevronLeftIcon fontSize="small" />
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        </>
      )}

      {/* ================= REPORTS TAB ================= */}
      {mainTab === 'reports' && (
        <>
          <div className="entity-filters">
            <div className="filters-grid">
              <div className="filter-group">
                <label className="filter-label">وضعیت گزارش</label>
                <select
                  className="filter-field"
                  value={reportStatusFilter}
                  onChange={(e) => {
                    setReportStatusFilter(e.target.value as StoryReportStatus | '');
                    setReportsPage(0);
                  }}
                >
                  {reportStatusOptions.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          <div className="entity-table-wrapper" style={{ padding: 14 }}>
            {reportsLoading && !reports.length ? (
              <div className="entity-loading">
                <CircularProgress size={32} />
              </div>
            ) : reports.length === 0 ? (
              <div className="entity-empty">
                <div className="entity-empty-icon">
                  <InboxIcon style={{ fontSize: 48 }} />
                </div>
                <p className="entity-empty-text">گزارشی یافت نشد</p>
              </div>
            ) : (
              <>
                {reports.map((report) => renderReportRow(report, false))}

                <div className="entity-pagination">
                  <div className="pagination-info">
                    نمایش {toPersianDigits(String(reportsTotal === 0 ? 0 : reportsPage * reportsPerPage + 1))} تا{' '}
                    {toPersianDigits(String(Math.min((reportsPage + 1) * reportsPerPage, reportsTotal)))} از{' '}
                    {toPersianDigits(String(reportsTotal))}
                  </div>
                  <div className="pagination-controls">
                    <select
                      className="pagination-select"
                      value={reportsPerPage}
                      onChange={(e) => {
                        setReportsPerPage(Number(e.target.value));
                        setReportsPage(0);
                      }}
                    >
                      <option value={10}>10</option>
                      <option value={20}>20</option>
                      <option value={50}>50</option>
                    </select>
                    <button
                      className="pagination-btn"
                      onClick={() => setReportsPage(reportsPage + 1)}
                      disabled={reportsPage >= reportsPages - 1}
                    >
                      <ChevronRightIcon fontSize="small" />
                    </button>
                    <span style={{ fontSize: 12 }}>
                      {toPersianDigits(String(reportsPage + 1))} / {toPersianDigits(String(reportsPages || 1))}
                    </span>
                    <button
                      className="pagination-btn"
                      onClick={() => setReportsPage(reportsPage - 1)}
                      disabled={reportsPage <= 0}
                    >
                      <ChevronLeftIcon fontSize="small" />
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        </>
      )}

      {/* ================= Lightbox ================= */}
      <Dialog
        open={!!lightboxStory || !!lightboxUrl}
        onClose={() => {
          setLightboxStory(null);
          setLightboxUrl(null);
        }}
        maxWidth="sm"
        fullWidth
      >
        <div className="edit-dialog-title">
          <span>
            {lightboxStory?.store?.name
              ? `استوری ${lightboxStory.store.name}`
              : 'پیش‌نمایش استوری'}
          </span>
          <button
            className="details-close"
            onClick={() => {
              setLightboxStory(null);
              setLightboxUrl(null);
            }}
          >
            <CloseIcon fontSize="small" />
          </button>
        </div>
        <DialogContent style={{ padding: 16, textAlign: 'center' }}>
          {(() => {
            const url = lightboxStory?.media?.url || lightboxUrl?.url;
            const type = lightboxStory?.media?.type || lightboxUrl?.type;
            if (!url) return <p style={{ fontSize: 13 }}>مدیا در دسترس نیست</p>;
            if (type === 'video') {
              return (
                <video src={url} controls autoPlay style={{ maxWidth: '100%', maxHeight: '70vh', borderRadius: 12 }} />
              );
            }
            return <img src={url} alt="" style={{ maxWidth: '100%', maxHeight: '70vh', borderRadius: 12 }} />;
          })()}
          {lightboxStory && (
            <div style={{ marginTop: 10, fontSize: 12, color: 'var(--color-text-secondary)', display: 'flex', gap: 14, justifyContent: 'center', flexWrap: 'wrap' }}>
              <span>{formatJalaliDateTime(lightboxStory.createdAt)}</span>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                <RemoveRedEyeIcon style={{ fontSize: 14 }} />
                {toPersianDigits(String(lightboxStory.viewersCount || 0))} بازدید
              </span>
              {lightboxStory.link?.url && (
                <a href={lightboxStory.link.url} target="_blank" rel="noreferrer" style={{ direction: 'ltr' }}>
                  {lightboxStory.link.url}
                </a>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* ================= Moderation dialog ================= */}
      <Dialog open={!!moderateStory} onClose={() => !moderating && setModerateStory(null)} maxWidth="xs" fullWidth>
        <div className="delete-dialog-content">
          <div className="delete-dialog-icon" style={{ background: moderateHide ? '#fee2e2' : '#dcfce7' }}>
            {moderateHide ? (
              <VisibilityOffIcon style={{ fontSize: 28, color: '#dc2626' }} />
            ) : (
              <PublishIcon style={{ fontSize: 28, color: '#16a34a' }} />
            )}
          </div>
          <h3 className="delete-dialog-title">
            {moderateHide ? 'خروج استوری از انتشار' : 'بازگرداندن استوری به انتشار'}
          </h3>
          <p className="delete-dialog-text">
            {moderateHide
              ? 'استوری از فید عمومی حذف می‌شود و گزارش‌های در انتظار آن «اقدام‌شده» علامت می‌خورند.'
              : 'استوری دوباره در فید عمومی نمایش داده می‌شود.'}
          </p>
          {moderateStory && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, justifyContent: 'center', marginBottom: 12 }}>
              <StoryThumb url={moderateStory.media?.url} type={moderateStory.media?.type} size={48} />
              <span style={{ fontSize: 13 }}>{moderateStory.store?.name || 'نامشخص'}</span>
            </div>
          )}
          {moderateHide && (
            <div className="edit-field" style={{ textAlign: 'right', margin: '0 0 16px' }}>
              <textarea
                value={moderateReason}
                onChange={(e) => setModerateReason(e.target.value)}
                placeholder="دلیل (اختیاری)..."
                rows={3}
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  border: '1px solid var(--color-border)',
                  borderRadius: 8,
                  fontFamily: 'inherit',
                  fontSize: '0.875rem',
                  resize: 'vertical',
                  boxSizing: 'border-box',
                  background: 'var(--color-bg-paper)',
                  color: 'var(--color-text-primary)'
                }}
              />
            </div>
          )}
          <div className="delete-dialog-actions">
            <button className="btn btn-ghost" onClick={() => setModerateStory(null)} disabled={moderating}>
              انصراف
            </button>
            <button
              className="btn btn-danger"
              style={!moderateHide ? { background: '#16a34a' } : undefined}
              onClick={handleModerate}
              disabled={moderating}
            >
              {moderating && <CircularProgress size={16} style={{ color: '#fff' }} />}
              {moderateHide ? 'خروج از انتشار' : 'بازگرداندن'}
            </button>
          </div>
        </div>
      </Dialog>

      {/* ================= Delete dialog ================= */}
      <Dialog open={!!deleteStory} onClose={() => !deleting && setDeleteStory(null)} maxWidth="xs" fullWidth>
        <div className="delete-dialog-content">
          <div className="delete-dialog-icon" style={{ background: '#fee2e2' }}>
            <DeleteIcon style={{ fontSize: 28, color: '#dc2626' }} />
          </div>
          <h3 className="delete-dialog-title">حذف استوری</h3>
          <p className="delete-dialog-text">
            استوری و فایل مدیای آن برای همیشه حذف می‌شوند (گزارش‌ها با snapshot باقی می‌مانند). این عمل قابل بازگشت نیست.
          </p>
          {deleteStory && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, justifyContent: 'center', marginBottom: 12 }}>
              <StoryThumb url={deleteStory.media?.url} type={deleteStory.media?.type} size={48} />
              <span style={{ fontSize: 13 }}>{deleteStory.store?.name || 'نامشخص'}</span>
            </div>
          )}
          <div className="delete-dialog-actions">
            <button className="btn btn-ghost" onClick={() => setDeleteStory(null)} disabled={deleting}>
              انصراف
            </button>
            <button className="btn btn-danger" onClick={handleDelete} disabled={deleting}>
              {deleting && <CircularProgress size={16} style={{ color: '#fff' }} />}
              <DeleteIcon fontSize="small" /> حذف
            </button>
          </div>
        </div>
      </Dialog>

      {/* ================= Per-story reports drawer ================= */}
      <Dialog
        open={!!reportsDrawerStory}
        onClose={() => setReportsDrawerStory(null)}
        maxWidth="md"
        fullWidth
      >
        <div className="edit-dialog-title">
          <span>
            گزارش‌های استوری {reportsDrawerStory?.store?.name ? `«${reportsDrawerStory.store.name}»` : ''}
          </span>
          <button className="details-close" onClick={() => setReportsDrawerStory(null)}>
            <CloseIcon fontSize="small" />
          </button>
        </div>
        <DialogContent className="edit-dialog-content">
          {drawerLoading ? (
            <div className="entity-loading">
              <CircularProgress size={28} />
            </div>
          ) : drawerReports.length === 0 ? (
            <div className="entity-empty">
              <div className="entity-empty-icon">
                <InboxIcon style={{ fontSize: 40 }} />
              </div>
              <p className="entity-empty-text">گزارشی برای این استوری ثبت نشده است</p>
            </div>
          ) : (
            drawerReports.map((report) => renderReportRow(report, true))
          )}
        </DialogContent>
        <div className="edit-dialog-actions">
          {reportsDrawerStory && !reportsDrawerStory.hidden && (
            <button
              className="edit-btn cancel"
              style={{ color: '#dc2626', borderColor: '#dc2626' }}
              onClick={() => {
                const s = reportsDrawerStory;
                setReportsDrawerStory(null);
                if (s) openModerate(s, true);
              }}
            >
              <VisibilityOffIcon fontSize="small" /> خروج از انتشار
            </button>
          )}
          <button className="edit-btn cancel" onClick={() => setReportsDrawerStory(null)}>
            بستن
          </button>
        </div>
      </Dialog>
    </div>
  );
};

export default StoryManagement;
